/**
 * Le panneau d'un Move. Il REMPLACE le clavier dans le dock tant qu'il est ouvert — on ne superpose
 * pas deux claviers, on en montre un. La croix en haut le referme et le clavier revient.
 *
 * Or = un savoir (une formule, un théorème : ça apporte une écriture).
 * Violet = un geste (remplacer, développer, factoriser, passer de l'autre côté : ça transforme).
 * Ce sont les deux seules couleurs du panneau ; le vert, le rouge et le doré de la feuille disent
 * juste / faux / HORA et n'ont rien à faire sur une touche qu'on n'a pas encore jouée.
 */
import { useState } from 'react';
import { MathText } from '@jeraisonne/ui';
import { moveMath } from './move-math';
import { moveById, type MoveDef, type MoveShelf } from './move-catalog';
import {
  applyLabel,
  backToShelf,
  openPanel,
  panelTitle,
  pickMove,
  pickWriting,
  shelfMoves,
  type MovePanelState,
} from './move-panel-state';
import './move-panel.css';

function KindBadge({ kind }: { kind: MoveDef['kind'] }) {
  return (
    <span className="jr-move-badge" data-kind={kind}>
      {kind === 'savoir' ? 'Savoir' : 'Geste'}
    </span>
  );
}

export function MovePanel({
  shelf,
  unlocked,
  onUnlock,
  onApply,
  onClose,
  state: given,
  onState,
}: {
  shelf: MoveShelf;
  /** les Moves déjà acquis */
  unlocked: ReadonlySet<string>;
  onUnlock: (moveId: string) => void;
  /** savoir : poser sa ligne sur la feuille ; geste : ouvrir sa famille de touches */
  onApply: (move: MoveDef) => void;
  onClose: () => void;
  /**
   * L'écran où en est le panneau. Absent : le panneau garde son état lui-même (cas normal).
   * Fourni : il est PILOTÉ de l'extérieur — la démo guidée le lève pour que le coach sache où
   * en est l'élève et montre la bonne touche du doigt.
   */
  state?: MovePanelState;
  onState?: (next: MovePanelState) => void;
}) {
  const [own, setOwn] = useState<MovePanelState>(() => openPanel(shelf, unlocked));
  const state = given ?? own;
  const setState = (next: MovePanelState) => {
    if (given) onState?.(next);
    else setOwn(next);
  };
  const move = state.step === 'shelf' ? null : moveById(state.moveId);

  return (
    <section className="jr-move" data-step={state.step} data-testid="move-panel">
      <header className="jr-move-head">
        <span className="jr-move-tag">Ma trousse</span>
        <h2 className="jr-move-title">{panelTitle(state, shelf)}</h2>
        <button
          type="button"
          className="jr-move-x"
          onClick={onClose}
          aria-label="Fermer la trousse"
        >
          ✕
        </button>
      </header>

      {state.step === 'shelf' && (
        <>
          <ul className="jr-move-shelf">
            {shelfMoves(shelf).map((m) => (
              <li key={m.id}>
                <button
                  type="button"
                  className="jr-move-item"
                  data-move={m.id}
                  data-kind={m.kind}
                  data-owned={unlocked.has(m.id) ? '1' : '0'}
                  onClick={() => setState(pickMove(shelf, m.id, unlocked))}
                >
                  <span className="jr-move-name">{m.name}</span>
                  <KindBadge kind={m.kind} />
                </button>
              </li>
            ))}
          </ul>
          {state.wrong && (
            <p className="jr-move-say" role="status">
              {state.wrong}
            </p>
          )}
        </>
      )}

      {state.step === 'unlock' && move && (
        <>
          <ul className="jr-move-choices">
            {move.choices.map((c, i) => (
              <li key={c.text}>
                <button
                  type="button"
                  className="jr-move-choice"
                  data-writing={i}
                  data-kind={move.kind}
                  // l'écriture est dessinée (fractions empilées) : le nom lu reste le texte du moteur
                  aria-label={c.text}
                  onClick={() => {
                    const r = pickWriting(move, i);
                    if (r.unlocked) onUnlock(r.unlocked);
                    setState(r.state);
                  }}
                >
                  <MathText>{moveMath(c.text)}</MathText>
                </button>
              </li>
            ))}
          </ul>
          {state.wrong && (
            <p className="jr-move-say" role="status">
              {state.wrong}
            </p>
          )}
          <button type="button" className="jr-move-back" onClick={() => setState(backToShelf())}>
            ← Autre chose
          </button>
        </>
      )}

      {state.step === 'apply' && move && (
        <div className="jr-move-apply" data-kind={move.kind}>
          {state.justUnlocked && (
            <p className="jr-move-won" role="status">
              Move débloqué : <strong>{move.name}</strong>
            </p>
          )}
          <p className="jr-move-why">{move.why}</p>
          {move.writes && (
            <p className="jr-move-line">
              <MathText>{moveMath(move.writes)}</MathText>
            </p>
          )}
          <button
            type="button"
            className="jr-move-go"
            data-testid="move-apply"
            data-kind={move.kind}
            onClick={() => onApply(move)}
          >
            {applyLabel(move)}
          </button>
        </div>
      )}
    </section>
  );
}
